import { useCallback, useEffect, useRef, useState } from 'react';
import {
  TextField,
  Typography,
  Chip,
  Box,
  CircularProgress,
} from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { computeMachineId } from '../../../utils/machineId';

const MAX_LENGTH = 64;
const ALLOWED = /^[a-zA-Z0-9 \-&,/'\u2019]*$/;
const RESERVED = ['admin', 'system', 'default', 'unassigned', 'root'];

export interface TeamNameFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Machine ID of the current entity — used to exclude self in edit mode */
  excludeMachineId?: string;
  label?: string;
  required?: boolean;
  /** Show the live Machine ID preview below the field (default true; set false in edit mode) */
  showMachineIdPreview?: boolean;
}

export function TeamNameField({
  value,
  onChange,
  excludeMachineId,
  label = 'Team Name',
  required = true,
  showMachineIdPreview = true,
}: TeamNameFieldProps) {
  const catalogApi = useApi(catalogApiRef);
  const [error, setError] = useState<string | undefined>();
  const [checking, setChecking] = useState(false);
  const [machineId, setMachineId] = useState('');
  const [dirty, setDirty] = useState(false);
  const [focused, setFocused] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const validate = useCallback(
    (name: string) => {
      const trimmed = name.trim().replace(/\s+/g, ' ');

      if (!trimmed) return undefined;
      if (trimmed.length < 2) return 'Minimum 2 characters';
      if (trimmed.length > MAX_LENGTH) return `Maximum ${MAX_LENGTH} characters`;
      if (!ALLOWED.test(trimmed))
        return 'Invalid characters — allowed: letters, numbers, spaces, - & , / \'';
      const lower = trimmed.toLowerCase();
      if (RESERVED.includes(lower)) return `"${trimmed}" is a reserved word`;
      const id = computeMachineId(trimmed);
      if (id.length < 2) return 'Name produces an invalid Machine ID (too short)';
      return undefined;
    },
    [],
  );

  useEffect(() => {
    const id = computeMachineId(value);
    setMachineId(id);

    const syncError = validate(value);
    if (syncError || !value.trim()) {
      setError(syncError);
      setChecking(false);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      return () => {};
    }

    // Debounced async uniqueness check (FR-004)
    setChecking(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await catalogApi.getEntities({
          filter: { kind: 'Group' },
          fields: ['metadata.name', 'metadata.title'],
        });
        const lower = value.trim().toLowerCase();
        const collision = response.items.find(
          e =>
            (e.metadata.title ?? '').toLowerCase() === lower &&
            e.metadata.name !== excludeMachineId,
        );
        if (collision) {
          setError(
            `A team named "${collision.metadata.title}" already exists`,
          );
        } else {
          setError(undefined);
        }
      } catch {
        setError(undefined); // fail open on catalog error
      } finally {
        setChecking(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, validate, catalogApi, excludeMachineId]);

  const suggestCasing = (suggestion: string) => () => onChange(suggestion);
  const titleCase = value
    .split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

  return (
    <Box>
      <TextField
        fullWidth
        label={label}
        required={required}
        value={value}
        onChange={e => { setDirty(true); onChange(e.target.value); }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        error={!!error}
        helperText={
          error ?? (focused ? `${value.length}/${MAX_LENGTH} characters` : undefined)
        }
        inputProps={{ maxLength: MAX_LENGTH + 10 }}
        InputProps={{
          endAdornment: checking ? (
            <CircularProgress size={16} />
          ) : undefined,
        }}
        variant="outlined"
      />

      {/* Live Machine ID preview (FR-006) — hidden in edit mode */}
      {showMachineIdPreview && machineId && (
        <Typography variant="caption" color="textSecondary">
          Machine ID: <strong>{machineId}</strong>
        </Typography>
      )}

      {/* Casing suggestion chip — only after user has typed */}
      {dirty && value && value !== titleCase && (
        <Box mt={0.5}>
          <Chip
            size="small"
            label={`Use: "${titleCase}"`}
            onClick={suggestCasing(titleCase)}
            variant="outlined"
          />
        </Box>
      )}
    </Box>
  );
}
